import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { MoneyInputError, formatWireForDisplay, parseMajorInput, type WireMoney } from '../money.js'
import type { PanelOption } from './PanelStatements.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Recording what a panel paid. §6.13 `CLM-004`; §15.20 step 5: "Payment received and allocated
 * across claims."
 *
 * ---
 *
 * **Two halves, and the aged report is not the lesser one.** A shopkeeper with a cheque in hand
 * needs to know what this panel owes and how old it is before entering anything, and that is
 * `CLM-004`'s aged claims report. Entering the cheque without it is how money lands on the wrong
 * claims. So the screen reads the ageing first, shows this panel's share of it, and offers the entry
 * underneath.
 *
 * **`oldest_first` and nothing else, deliberately.** The service takes an explicit plan too, and an
 * explicit plan needs the panel's open claims with their outstanding figures to choose between, each
 * one named and amountable. The aged report carries a claim id and an open total and no order, no
 * date and no customer, so a chooser built from it would ask a shopkeeper to allocate money against
 * eight bare identifiers. `oldest_first` is what a shop does with a cheque anyway and what the
 * statement invites, and it is the plan the server will accept with nothing guessed: it has no
 * default, and this screen is what sends one.
 *
 * **It pre-checks what it can see and nothing else** (`VoidSale.tsx` sets out the rule). A refused
 * write sits `blocked` at the head of the write queue and stops every write behind it (`OFF-001a`,
 * `OFF-005`), so a positive amount, a chosen panel and a real date are checked here, while the
 * refusals that depend on stored rows (an unknown panel, a superseded statement, a claim at a branch
 * this session does not hold) are the server's and reach the counter as its own sentence. What the
 * screen will not do is offer a button whose refusal it could have known about.
 *
 * **A panel under the reports section rather than a rail destination.** All twenty-six rail glyphs
 * are taken and `rail.test.ts` enforces it (`decisions/0026`, `decisions/0040`). It sits beside
 * `PanelStatements`, which is the document this money is paid against, and takes a narrower row:
 * `panel_remittance_record` rather than `report_financial`, because reading what a panel owes is not
 * the authority to write what it paid.
 *
 * **Reversing a bounced cheque is not here**, and the route exists. Nothing in the API lists a
 * panel's payments, so this screen cannot name the cheque to reverse nor pre-check the three
 * refusals only the stored rows know, and offering it would be the queue-stopper the paragraph above
 * is about. `remittances.controller.ts` carries the route and says the same.
 */

// ---------------------------------------------------------------------------------------------
// What the read sends. Shaped to `claims.service.ts`, not imported from it: `apps/*` never import
// from each other.
// ---------------------------------------------------------------------------------------------

export interface AgedBucketData {
  /** The domain's own label, `0-30`, `31-60`, `91+`. Digits, so it is read LTR in Urdu too. */
  readonly label: string
  readonly claimCount: number
  readonly open: WireMoney
}

export interface AgedRowData {
  readonly claimId: string
  readonly panelId: string
  readonly open: WireMoney
  /** Absent on a claim never submitted: there is no clock to age it from. */
  readonly ageDays?: number
  readonly bucket?: number
}

export interface AgedData {
  readonly buckets: readonly AgedBucketData[]
  readonly notYetSubmitted: { readonly claimCount: number; readonly open: WireMoney }
  readonly rows: readonly AgedRowData[]
}

export interface PanelRemittanceProps {
  readonly loadPanels: () => Promise<readonly PanelOption[]>
  /** `GET /claims/aged`, in the panel's own currency. */
  readonly loadAged: (currency: string) => Promise<AgedData>
  /** Appendix B's `panel_remittance_record`. The three routes check the same row. */
  readonly canRecord: boolean
  /** The branch's own today, which is what a cheque received now is dated. */
  readonly today: string
  readonly onRecorded?: () => void
}

const show = (value: WireMoney): string => formatWireForDisplay(value.minor, value.currency)

/** Minor units summed as text, because a wire figure is a decimal string and never a float. */
const totalOf = (rows: readonly AgedRowData[]): bigint =>
  rows.reduce((sum, row) => sum + BigInt(row.open.minor), 0n)

export function PanelRemittance({
  loadPanels,
  loadAged,
  canRecord,
  today,
  onRecorded,
}: PanelRemittanceProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [panels, setPanels] = useState<readonly PanelOption[] | null>(null)
  const [panelsFailed, setPanelsFailed] = useState(false)
  const [panelId, setPanelId] = useState('')
  const [aged, setAged] = useState<AgedData | null>(null)
  const [agedFailed, setAgedFailed] = useState(false)
  const [amountText, setAmountText] = useState('')
  const [receivedOn, setReceivedOn] = useState(today)
  const [reference, setReference] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)

  /**
   * **Registered after the gate, not before it.** `PanelStatements` had this the other way round,
   * so mounted ungated it fired `GET /panels` for a cashier and took a 403 on a read nobody asked
   * for. The hooks have to run unconditionally, so the gate is inside the effect rather than above
   * it, and `panel-remittance.test.tsx` asserts that neither read is called rather than that
   * nothing renders. `app-reachability.test.tsx` now has the pair that drives it from the rail,
   * and the negative there records what the mutations showed: this gate and the one at the
   * mount site are each sufficient on their own, so only removing both is observable.
   */
  useEffect(() => {
    if (!canRecord) return
    let cancelled = false
    void loadPanels()
      .then((rows) => {
        if (!cancelled) setPanels(rows)
      })
      .catch(() => {
        if (!cancelled) setPanelsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [canRecord, loadPanels])

  const panel = (panels ?? []).find((row) => row.id === panelId)

  const refresh = useCallback(() => {
    if (!canRecord || panel === undefined) {
      setAged(null)
      return () => undefined
    }
    let cancelled = false
    setAgedFailed(false)
    setAged(null)
    void loadAged(panel.currency)
      .then((answer) => {
        if (!cancelled) setAged(answer)
      })
      .catch(() => {
        if (!cancelled) setAgedFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [canRecord, loadAged, panel])

  useEffect(() => refresh(), [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setConfirming(false)
    setAmountText('')
    setReference('')
    refresh()
    onRecorded?.()
  })

  /** This panel's share of the ageing. The report is per currency, so the rows carry the panel. */
  const mine = useMemo(
    () => (aged?.rows ?? []).filter((row) => row.panelId === panelId),
    [aged, panelId],
  )

  /**
   * The amount in minor units, or the reason it is not one.
   *
   * `parseMajorInput` is the shared parser and is currency-aware, so a paisa figure typed into a
   * zero-decimal currency is refused in one place rather than in each screen's own regex.
   */
  const parsed = useMemo((): { readonly minor: bigint } | { readonly error: string } => {
    if (amountText.trim() === '') return { error: '' }
    try {
      const minor = parseMajorInput(amountText, panel?.currency ?? 'PKR')
      if (minor <= 0n) return { error: t('panelRemittance.amountNotPositive') }
      return { minor }
    } catch (error) {
      if (error instanceof MoneyInputError) return { error: t('panelRemittance.amountNotAmount') }
      throw error
    }
  }, [amountText, panel, t])

  const amountMinor = 'minor' in parsed ? parsed.minor : null
  const dateIsReal = /^\d{4}-\d{2}-\d{2}$/.test(receivedOn)
  const ready = panel !== undefined && amountMinor !== null && dateIsReal

  const record = useCallback(async () => {
    if (panel === undefined || amountMinor === null) return
    const write = (await enqueue({
      method: 'POST',
      path: `/panels/${encodeURIComponent(panel.id)}/payments`,
      label: t('panelRemittance.queueLabel', { panel: panel.name }),
      body: {
        receivedOn,
        amountMinor: amountMinor.toString(),
        ...(reference.trim() === '' ? {} : { reference: reference.trim() }),
        // Named in full rather than left out. The server has no default and refuses
        // `PLAN_REQUIRED` without one, which is the right refusal and not one to walk into.
        plan: { kind: 'oldest_first' },
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [amountMinor, enqueue, panel, receivedOn, reference, t])

  if (!canRecord) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.panelRemittance'))
  const openTotal = totalOf(mine)
  const currency = panel?.currency ?? ''

  return (
    <section className="lm-screen" aria-labelledby="panel-remittance-heading">
      <h1 id="panel-remittance-heading" className="lm-screen__heading">
        {t('panelRemittance.heading')}
      </h1>
      <p className="lm-hint">{t('panelRemittance.whatItIs')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('panelRemittance.panelLabel')}</span>
        <select
          className="lm-input"
          value={panelId}
          onChange={(e) => {
            setPanelId(e.currentTarget.value)
            setConfirming(false)
          }}
        >
          <option value="">{t('panelRemittance.panelUnchosen')}</option>
          {(panels ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.deactivated
                ? t('panelRemittance.panelDeactivated', { panel: row.name })
                : row.name}
            </option>
          ))}
        </select>
      </label>
      {panelsFailed ? <p className="lm-error">{t('panelRemittance.panelsFailed')}</p> : null}

      {panel === undefined ? (
        <p className="lm-hint">{t('panelRemittance.chooseAPanel')}</p>
      ) : (
        <>
          <h2 className="lm-screen__subheading">{t('panelRemittance.agedHeading')}</h2>
          <DataSurface
            state={
              agedFailed
                ? {
                    kind: 'error',
                    message: t('panelRemittance.agedFailed'),
                    onRetry: () => {
                      refresh()
                    },
                  }
                : aged === null
                  ? { kind: 'loading' }
                  : mine.length === 0
                    ? { kind: 'empty' }
                    : { kind: 'ready' }
            }
            shape="rows"
            rows={3}
            what={t('panelRemittance.what')}
            emptyMessage={t('panelRemittance.nothingOpen')}
          >
            <ul className="lm-remittance__buckets">
              {(aged?.buckets ?? []).map((bucket) => (
                <li key={bucket.label} className="lm-remittance__bucket">
                  {/* `LOC-001`: western digits everywhere and never mirrored, including in Urdu. */}
                  <span dir="ltr">
                    <Ltr>{bucket.label}</Ltr>
                  </span>
                  <span className="lm-remittance__bucket-open">{show(bucket.open)}</span>
                </li>
              ))}
            </ul>
            <p className="lm-remittance__total">
              {t('panelRemittance.openTotal', {
                panel: panel.name,
                amount: formatWireForDisplay(openTotal.toString(), currency),
                count: mine.length,
              })}
            </p>
            {aged !== null && aged.notYetSubmitted.claimCount > 0 ? (
              <p className="lm-hint">
                {t('panelRemittance.notYetSubmitted', {
                  count: aged.notYetSubmitted.claimCount,
                })}
              </p>
            ) : null}
          </DataSurface>

          <h2 className="lm-screen__subheading">{t('panelRemittance.recordHeading')}</h2>

          <label className="lm-field">
            <span className="lm-field__label">
              {t('panelRemittance.amountLabel', { currency })}
            </span>
            <input
              className="lm-input"
              inputMode="decimal"
              dir="ltr"
              value={amountText}
              onChange={(e) => {
                setAmountText(e.currentTarget.value)
                setConfirming(false)
              }}
            />
          </label>
          {'error' in parsed && parsed.error !== '' ? (
            <p className="lm-field__error">{parsed.error}</p>
          ) : null}

          <label className="lm-field">
            <span className="lm-field__label">{t('panelRemittance.receivedOnLabel')}</span>
            <input
              className="lm-input"
              type="date"
              dir="ltr"
              value={receivedOn}
              onChange={(e) => {
                setReceivedOn(e.currentTarget.value)
                setConfirming(false)
              }}
            />
          </label>
          {dateIsReal ? null : <p className="lm-field__error">{t('panelRemittance.dateNeeded')}</p>}

          <label className="lm-field">
            <span className="lm-field__label">{t('panelRemittance.referenceLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
            />
          </label>
          <p className="lm-hint">{t('panelRemittance.referenceHint')}</p>

          {/*
            The spread is stated before the button rather than after the write, because
            `oldest_first` is a decision about somebody's money and a shopkeeper should read it
            before pressing anything. The server's answer carries the allocations it actually made.
          */}
          <p className="lm-hint">{t('panelRemittance.oldestFirst')}</p>

          {confirming && amountMinor !== null ? (
            <>
              <p className="lm-field__warning">
                {t('panelRemittance.confirm', {
                  panel: panel.name,
                  amount: formatWireForDisplay(amountMinor.toString(), currency),
                })}
              </p>
              {/*
                **`aged !== null` first, and not for tidiness.** With the ageing still loading or
                failed, `openTotal` is zero, so every amount would warn that it is more than the
                panel owes. A warning that is wrong whenever a read is slow is a warning a
                shopkeeper learns to click past, which is worse than not having it.
              */}
              {aged !== null && amountMinor > openTotal ? (
                <p className="lm-field__warning">
                  {t('panelRemittance.moreThanOpen', {
                    amount: formatWireForDisplay((amountMinor - openTotal).toString(), currency),
                  })}
                </p>
              ) : null}
              <div className="lm-screen__actions">
                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  disabled={writeId !== null}
                  onClick={() => {
                    void record()
                  }}
                >
                  {t('panelRemittance.confirmButton')}
                </button>
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => setConfirming(false)}
                >
                  {t('panelRemittance.cancel')}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="lm-button lm-button--primary"
              disabled={!ready}
              onClick={() => setConfirming(true)}
            >
              {t('panelRemittance.record')}
            </button>
          )}

          <p className="lm-queue-state" role="status">
            {line}
          </p>
        </>
      )}
    </section>
  )
}
